import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { CourseContext } from '../contexts/CourseContext';
import '../styles/CourseDetailsPage.css';

const PASSING_THRESHOLD = 75; // 75% passing score

export default function CourseDetailsPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCourseById, isAuthorizedCourse, coursesLoaded } = useContext(CourseContext);
  const warnedUnauthorizedCourseIdsRef = useRef(new Set());
  
  // Get course name from context (if course is loaded) or location state (fallback)
  const courseData = getCourseById(courseId);
  const courseName = courseData?.fullname || location.state?.courseName || 'Course';
  
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupInput, setGroupInput] = useState('');
  const [pendingGroupId, setPendingGroupId] = useState('');
  const [students, setStudents] = useState([]);
  const [assessmentMode, setAssessmentMode] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasGroupInput = groupInput.trim().length > 0;
  const isGroupInputInvalid = hasGroupInput && !pendingGroupId;

  const sortedGroups = useMemo(() => {
    const isNumericText = (value) => /^\d+$/.test(String(value || '').trim());

    return [...groups].sort((left, right) => {
      const leftName = String(left?.name || '').trim();
      const rightName = String(right?.name || '').trim();
      const leftIsNumeric = isNumericText(leftName);
      const rightIsNumeric = isNumericText(rightName);

      if (leftIsNumeric && rightIsNumeric) {
        return Number(rightName) - Number(leftName);
      }

      return rightName.localeCompare(leftName, undefined, { sensitivity: 'base' });
    });
  }, [groups]);

  const formatGroupLabel = (group) => {
    const count = Number(group?.memberCount) || 0;
    return `${group?.name || 'Group'} (${count} student${count !== 1 ? 's' : ''})`;
  };

  const resolveGroupId = (inputValue) => {
    const raw = String(inputValue || '').trim();
    if (!raw) {
      return '';
    }

    const exactId = groups.find((g) => String(g.id) === raw);
    if (exactId) {
      return String(exactId.id);
    }

    const lowerRaw = raw.toLowerCase();
    const exactName = groups.find((g) => String(g.name || '').trim().toLowerCase() === lowerRaw);
    if (exactName) {
      return String(exactName.id);
    }

    // Supports selecting labels like "Group Name (25 students)" from the datalist.
    const nameWithCount = raw.match(/^(.*)\s+\(\d+\s+student(?:s)?\)$/i);
    if (nameWithCount?.[1]) {
      const parsedName = nameWithCount[1].trim().toLowerCase();
      const matchByParsedName = groups.find((g) => String(g.name || '').trim().toLowerCase() === parsedName);
      if (matchByParsedName) {
        return String(matchByParsedName.id);
      }
    }

    return '';
  };

  const handleGroupInputChange = (value) => {
    const resolvedId = resolveGroupId(value);
    setGroupInput(value);
    setPendingGroupId(resolvedId);
    setSelectedGroup('');
    setStudents([]);
    if (error) {
      setError('');
    }
  };

  const handleSelectGroup = () => {
    if (!pendingGroupId) {
      setError('Please select a valid group from the list or enter an exact class number');
      return;
    }

    setSelectedGroup(pendingGroupId);
  };

  const handleClearGroupSelection = () => {
    setGroupInput('');
    setPendingGroupId('');
    setSelectedGroup('');
    setStudents([]);
    setError('');
  };

  useEffect(() => {
    if (!coursesLoaded) {
      return;
    }

    if (isAuthorizedCourse(courseId)) {
      return;
    }

    const courseKey = String(courseId);
    if (warnedUnauthorizedCourseIdsRef.current.has(courseKey)) {
      return;
    }

    warnedUnauthorizedCourseIdsRef.current.add(courseKey);
    console.warn(`[COURSE-DETAILS] Unauthorized course access attempt: ${courseId}`);
  }, [coursesLoaded, courseId, isAuthorizedCourse]);

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, [courseId]);

  // Load students when group changes
  useEffect(() => {
    if (selectedGroup) {
      loadStudents();
    } else {
      setStudents([]);
    }
  }, [selectedGroup]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch groups
      const groupsRes = await apiClient.get(`/api/courses/${courseId}/groups`);
      const fetchedGroups = groupsRes.data.groups || [];
      setGroups(fetchedGroups);

      if (selectedGroup) {
        const selected = fetchedGroups.find((g) => String(g.id) === String(selectedGroup));
        if (selected) {
          setGroupInput(formatGroupLabel(selected));
          setPendingGroupId(String(selected.id));
        }
      }
      
      console.log(`Loaded ${groupsRes.data.groups?.length || 0} groups for course ${courseId}`);
    } catch (err) {
      console.error('Error loading groups:', err);
      setError(err.response?.data?.message || 'Failed to load groups');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    if (!selectedGroup) return;

    try {
      setLoading(true);
      setError('');

      const response = await apiClient.get(
        `/api/courses/${courseId}/groups/${selectedGroup}/members`
      );

      // Sort students by lastname, firstname (already sorted by backend)
      setStudents(response.data.members || []);
      setAssessmentMode(response.data.assessmentMode || 'standard');
      console.log(`Loaded ${response.data.members?.length || 0} students for group ${selectedGroup}`);
    } catch (err) {
      console.error('Error loading students:', err);
      setError(err.response?.data?.message || 'Failed to load students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    const reportGroupId = selectedGroup || pendingGroupId;
    const selectedGroupRecord = groups.find((g) => String(g.id) === String(reportGroupId));
    const reportGroupName = selectedGroupRecord?.name || groupInput || 'Group';

    if (isGroupInputInvalid) {
      setError('Please select a valid group from the list or enter an exact class number');
      return;
    }

    if (!reportGroupId) {
      setError('Please select a group first, then generate the report');
      return;
    }

    setError('');

    const searchParams = new URLSearchParams({
      groupId: String(reportGroupId),
      groupName: String(reportGroupName),
      courseName: String(courseName)
    });

    navigate(`/course/${courseId}/remediation-report?${searchParams.toString()}`);
  };

  if (loading && groups.length === 0) {
    return <div className="loading">Loading groups for course {courseId}...</div>;
  }

  if (error && groups.length === 0) {
    return (
      <div className="course-details-error">
        <h2>Error Loading Groups</h2>
        <p>{error}</p>
        <p style={{fontSize: '0.9em', color: '#666'}}>Course ID: {courseId}</p>
        <button onClick={() => {
          setError('');
          loadGroups();
        }}>Retry</button>
        <button onClick={() => navigate('/')}>Back to Courses</button>
      </div>
    );
  }

  return (
    <div className="course-details">
      <header className="course-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Courses
        </button>
        <h1>{courseName}</h1>
      </header>

      <main className="course-main">

        <section className="group-selection">
          <div className="selection-card">
            <h3>Select a Class Group</h3>
            
            {groups.length === 0 ? (
              <p className="no-groups">No groups found for this course.</p>
            ) : (
              <>
                <label htmlFor="group-dropdown">Available Groups:</label>
                <input
                  id="group-dropdown"
                  list="group-options"
                  value={groupInput}
                  onChange={(e) => handleGroupInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSelectGroup();
                    }
                  }}
                  className="group-dropdown"
                  placeholder="Type group name or class number, or pick from the list"
                />
                <datalist id="group-options">
                  {sortedGroups.map((group) => (
                    <option key={group.id} value={formatGroupLabel(group)} />
                  ))}
                </datalist>

                {isGroupInputInvalid && (
                  <p className="group-input-error">
                    Group not recognized. Use a suggestion, exact group name, or class number.
                  </p>
                )}

                <div className="group-select-actions">
                  <button
                    type="button"
                    className="btn-select-group"
                    onClick={handleSelectGroup}
                    disabled={!pendingGroupId}
                  >
                    Select Group
                  </button>
                  <button
                    type="button"
                    className="btn-select-group"
                    onClick={handleClearGroupSelection}
                    disabled={!groupInput && !pendingGroupId && !selectedGroup && students.length === 0 && !error}
                  >
                    Clear
                  </button>
                </div>

                {selectedGroup && (
                  <div className="group-info">
                    <p className="group-selected">
                      {(() => {
                        const selected = groups.find(g => String(g.id) === String(selectedGroup));
                        const selectedCount = Number(selected?.memberCount) || 0;
                        return (
                          <>
                            Selected: <strong>{selected?.name}</strong> ({selectedCount} student{selectedCount !== 1 ? 's' : ''})
                          </>
                        );
                      })()}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {selectedGroup && (
          <section className="students-section">
            <div className="students-card">
              <h3>Students in Selected Group</h3>
              
              {loading ? (
                <p className="loading">Loading students...</p>
              ) : students.length === 0 ? (
                <p className="no-students">No students in this group.</p>
              ) : (
                <>
                  <p className="student-count">
                    {students.length} student{students.length !== 1 ? 's' : ''} enrolled
                  </p>
                  <div className="student-list">
                    <div className={`student-header ${assessmentMode === 'module11-split' ? 'student-header-split' : ''}`}>
                      <span className="col-name">Name</span>
                      {assessmentMode === 'module11-split' ? (
                        <>
                          <span className="col-score split-score-header">Knowledge</span>
                          <span className="col-score split-score-header">Performance</span>
                        </>
                      ) : (
                        <span className="col-score">Score</span>
                      )}
                    </div>
                    {students.map((student) => {
                      const scoreNum = parseFloat(student.score) || 0;
                      const isFailing = scoreNum < PASSING_THRESHOLD;
                      const knowledgeScore = Number.isFinite(Number(student.knowledgeScore)) ? Number(student.knowledgeScore) : null;
                      const performanceScore = Number.isFinite(Number(student.performanceScore)) ? Number(student.performanceScore) : null;
                      return (
                        <div key={student.id} className={`student-item ${assessmentMode === 'module11-split' ? 'student-item-split' : ''} ${isFailing ? 'failing-score' : ''}`}>
                          <span className="col-name">{student.lastname}, {student.firstname}</span>
                          {assessmentMode === 'module11-split' ? (
                            <>
                              <span className={`col-score ${knowledgeScore !== null && knowledgeScore < PASSING_THRESHOLD ? 'score-failing' : 'score-passing'}`}>
                                {knowledgeScore !== null ? knowledgeScore.toFixed(2) : '-'}
                              </span>
                              <span className={`col-score ${performanceScore !== null && performanceScore < PASSING_THRESHOLD ? 'score-failing' : 'score-passing'}`}>
                                {performanceScore !== null ? performanceScore.toFixed(2) : '-'}
                              </span>
                            </>
                          ) : (
                            <span className={`col-score ${isFailing ? 'score-failing' : 'score-passing'}`}>
                              {scoreNum.toFixed(2)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {error && <div className="error-message">{error}</div>}

        {(selectedGroup || pendingGroupId) && !loading && (
          <section className="report-actions">
            <button
              className="btn-generate-report"
              onClick={handleGenerateReport}
              disabled={loading || isGroupInputInvalid}
            >
              {loading ? 'Generating...' : 'Generate Remediation Report'}
            </button>
          </section>
        )}
      </main>
    </div>
  );
}
